import { SelectProps } from "@radix-ui/react-select";
import { useState, useMemo, useEffect, useRef } from "react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import Loader from "./Loader";

type SelectOptionValue = string | number | null;

interface SelectOption {
	label: string;
	value: SelectOptionValue;
}

export interface StyledSelectProps extends SelectProps {
	label?: string;
	placeholder?: string;
	error?: string;
	options?: SelectOption[];
	searchable?: boolean;
	searchPlaceholder?: string;
	isLoading?: boolean;
}

export const StyledSelect = (props: StyledSelectProps) => {
	const { 
		label, 
		placeholder, 
		options, 
		error, 
		required, 
		searchable = false,
		searchPlaceholder = "Rechercher...",
		value,
		onValueChange,
		isLoading,
		...selectProps 
	} = props;

	const [searchTerm, setSearchTerm] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const selectedOption = useMemo(() => {
		if (!value) return undefined;
		return options?.find(option => option.value?.toString() === value?.toString());
	}, [options, value]);

	const filteredOptions = useMemo(() => {
		if (!searchable || !searchTerm.trim()) {
			return options || [];
		}
		
		return (options || []).filter((option) =>
			option.label.toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [options, searchTerm, searchable]);


	useEffect(() => {
		if (!isOpen) {
			setSearchTerm("");
		}
	}, [isOpen]);

	// Focus automatique sur l'input quand le dropdown s'ouvre (mode recherche uniquement)
	useEffect(() => {
		if (isOpen && searchable && inputRef.current) {

			const timeoutId = setTimeout(() => {
				if (inputRef.current) {
					inputRef.current.focus();
		
					const length = inputRef.current.value.length;
					inputRef.current.setSelectionRange(length, length);
				}
			}, 50);
			return () => clearTimeout(timeoutId);
		}
		return undefined;
	}, [isOpen, searchable]);

	const handleSelect = (selectedValue: string) => {
		if (selectedValue === "") {
			return;
		}
		onValueChange?.(selectedValue);
		setIsOpen(false);
		setSearchTerm("");
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			setSearchTerm("");
		} else if (searchable) {
	
			setSearchTerm("");
		}
	};

	const handleInputClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!isOpen) {
			setIsOpen(true);
		} else {

			if (inputRef.current) {
				const length = inputRef.current.value.length;
				inputRef.current.setSelectionRange(length, length);
			}
		}
	};

	const handleInputKeyDown = (e: React.KeyboardEvent) => {
		e.stopPropagation();
		
		if (e.key === 'Escape') {
			setIsOpen(false);
		} else if (e.key === 'Enter') {
			e.preventDefault();
			const firstOption = filteredOptions[0];
			if (firstOption && firstOption.value != null) {
				handleSelect(firstOption.value.toString());
			}
		} else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
	
			e.preventDefault();
		}
	};

	return (
		<div className="*:not-first:mt-2 w-full">
			{label && (
				<Label>
					{label} {required && <span className="text-destructive">*</span>}
				</Label>
			)}
			<Select 
				{...selectProps} 
				value={value || undefined}
				onValueChange={handleSelect}
				open={isOpen}
				onOpenChange={handleOpenChange}
			>
				<SelectTrigger className="cursor-pointer">
					{searchable ? (
						<Input
							ref={inputRef}
							type="text"
							placeholder={isOpen ? (searchPlaceholder || placeholder) : (selectedOption?.label || placeholder)}
							value={isOpen ? searchTerm : ""}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyDown={handleInputKeyDown}
							onClick={handleInputClick}
							className="border-0 outline-none shadow-none p-0 m-0 h-auto w-full bg-transparent focus:outline-none focus:ring-0 focus:border-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none placeholder:text-muted-foreground cursor-text"
							readOnly={!isOpen}
						/>
					) : (
						<SelectValue placeholder={placeholder} />
					)}
					{isLoading && <Loader />}
				</SelectTrigger>
				<SelectContent>
					{filteredOptions.length > 0 ? (
						filteredOptions
							.filter(option => option.value != null && option.value !== "")
							.map((option) => (
								<SelectItem
									key={option.value ?? ""}
									value={option.value?.toString() ?? ""}
								>
									{option.label}
								</SelectItem>
							))
					) : searchable && searchTerm ? (
						<div className="px-2 py-1.5 text-sm text-muted-foreground">
							Aucun résultat trouvé
						</div>
					) : null}
				</SelectContent>
			</Select>
			{error && <p
				className="text-destructive mt-2 text-xs"
				role="alert"
				aria-live="polite"
			>
				{error}
			</p>}
		</div>
	);
};
